import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { ChatCircleDots, X, PaperPlaneRight, Sparkle, Headset, EnvelopeSimple, UserCircle } from '@phosphor-icons/react';
import { openSupportForm, supportMailto, SUPPORT_EMAIL } from '@/components/SupportContactModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Signals that the assistant isn't cutting it and a human should take over.
const FRUSTRATION = /\b(a )?(human|real person|agent|representative|someone real)\b|\b(speak|talk) to (someone|a person|a human)\b|\b(useless|pointless|stupid|dumb|garbage|terrible|awful|ridiculous|nonsense)\b|\b(not|isn'?t|doesn'?t|didn'?t|won'?t) (help|working|work)\b|\b(fed up|frustrat\w*|annoy\w*|angry|furious|pissed)\b|\b(scam|rip ?off|complain\w*|refund me)\b|\b(wtf|fml)\b/i;
const MAX_TURNS_BEFORE_OFFER = 5;   // long unresolved thread → offer a human

const GREETING = "Hi! I'm the Bookr Assistant 👋 Ask me anything about bookings, payments, cancellations, or becoming a provider.";
const SUGGESTIONS = [
  'Is my payment protected?',
  'How do refunds & cancellations work?',
  'How do I become a provider?',
  'How do I message a provider?',
];

// Pages that already have a full-height chat/input at the bottom — hide the
// floating bubble there so it doesn't overlap.
const HIDDEN_ON = ['/ai-planner', '/messages', '/coordinator'];

export default function SupportWidget() {
  const { token } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [escalated, setEscalated] = useState(false); // show the human hand-off card
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const hidden = HIDDEN_ON.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    const convo = [...messages, { role: 'user', content }];
    setMessages(convo);
    setInput('');
    setLoading(true);

    // Hand off to a human if they sound fed up, or the thread is dragging on.
    const turns = convo.filter((m) => m.role === 'user').length;
    if (FRUSTRATION.test(content) || turns >= MAX_TURNS_BEFORE_OFFER) setEscalated(true);

    try {
      // The API needs history to start with a user turn — drop the leading greeting.
      const firstUser = convo.findIndex(m => m.role === 'user');
      const apiMessages = firstUser >= 0 ? convo.slice(firstUser) : convo;
      const r = await axios.post(
        `${API}/support/chat`,
        { messages: apiMessages },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setMessages(m => [...m, { role: 'assistant', content: r.data.reply || "Sorry, I didn't catch that — could you try again?" }]);
    } catch (err) {
      // The assistant is down — escalate to a human rather than dead-ending.
      setEscalated(true);
      setMessages(m => [...m, { role: 'assistant', content: "Sorry — I can't reach my brain right now. Rather than leave you stuck, let's get a real person on it." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (hidden) return null;
  const started = messages.some(m => m.role === 'user');

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] flex flex-col items-end" data-testid="support-widget">
      {open && (
        <div
          className="mb-3 w-[min(92vw,380px)] h-[min(72vh,560px)] bg-card border border-border/70 rounded-2xl shadow-lift flex flex-col overflow-hidden fade-up"
          role="dialog" aria-label="Bookr support assistant"
          data-testid="support-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground shrink-0">
            <span className="w-9 h-9 rounded-full bg-primary-foreground/15 flex items-center justify-center shrink-0">
              <Headset size={18} weight="fill" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight">Bookr Assistant</p>
              <p className="text-[11px] text-primary-foreground/80 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" /> Typically replies instantly
              </p>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close support chat" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-primary-foreground/15 transition-colors cursor-pointer" data-testid="support-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background" data-testid="support-messages">
            {messages.map((m, i) => (
              m.role === 'assistant' ? (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkle size={12} weight="fill" />
                  </span>
                  <div className="bg-secondary/60 rounded-2xl rounded-tl-md px-3.5 py-2.5 text-[13px] text-foreground/90 leading-relaxed max-w-[85%] whitespace-pre-wrap">
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex justify-end">
                  <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-3.5 py-2.5 text-[13px] leading-relaxed max-w-[85%] whitespace-pre-wrap">
                    {m.content}
                  </div>
                </div>
              )
            ))}
            {loading && (
              <div className="flex items-start gap-2" data-testid="support-typing">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkle size={12} weight="fill" />
                </span>
                <div className="bg-secondary/60 rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {/* Human hand-off — appears when the AI fails or the person is clearly fed up */}
            {escalated && !loading && (
              <div className="rounded-2xl border-2 border-primary/25 bg-primary/5 p-3.5 mt-1" data-testid="support-escalation">
                <div className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <UserCircle size={15} weight="fill" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold text-foreground leading-tight">Talk to a real person</p>
                    <p className="text-[12px] text-muted-foreground leading-relaxed mt-0.5">
                      Sorry the assistant isn't getting you there. Milo reads every message personally.
                    </p>
                  </div>
                </div>
                <button onClick={() => openSupportForm('Support request from the assistant')}
                  className="w-full mt-3 bg-primary text-primary-foreground rounded-xl h-10 text-[13px] font-semibold btn-anim shadow-soft hover:bg-primary/90 cursor-pointer flex items-center justify-center gap-2"
                  data-testid="support-escalate-cta">
                  <Headset size={15} weight="fill" /> Contact a human
                </button>
                <a href={supportMailto('Bookr.ai support')} className="block text-center text-[11px] text-muted-foreground hover:text-primary mt-2 cursor-pointer" data-testid="support-escalate-mail">
                  or email {SUPPORT_EMAIL}
                </a>
              </div>
            )}

            {/* Suggestion chips (before the first question) */}
            {!started && !loading && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="support-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)}
                    className="text-[12px] font-medium text-primary border border-primary/30 bg-primary/5 rounded-full px-3 py-1.5 btn-anim hover:bg-primary/10 cursor-pointer">
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border/70 p-3 bg-card shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Ask a question…"
                className="flex-1 resize-none max-h-24 rounded-xl border border-border bg-background px-3 py-2.5 text-[13px] text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="support-input"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                aria-label="Send message"
                className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-40 cursor-pointer shrink-0"
                data-testid="support-send"
              >
                <PaperPlaneRight size={16} weight="fill" />
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-2">
              AI assistant ·{' '}
              <button type="button" onClick={() => { setEscalated(true); openSupportForm('Support request from the assistant'); }}
                className="text-primary font-semibold hover:underline cursor-pointer" data-testid="support-human-link">
                talk to a human
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Launcher bubble */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close support chat' : 'Open support chat'}
        aria-expanded={open}
        className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lift flex items-center justify-center btn-anim hover:bg-primary/90 hover:shadow-xl cursor-pointer"
        data-testid="support-launcher"
      >
        {open ? <X size={24} /> : <ChatCircleDots size={26} weight="fill" />}
      </button>
    </div>
  );
}
